import type { AssetTranscript, LanguageEnum, VoiceoverAssetResponse } from 'js/src/client';
import { ApiService, AsyncOperationNameEnum, TaskResultMessageTypeEnum } from 'js/src/client';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { SCREEN_WIDTH_THRESHOLD } from 'js/src/libs/video-data-consts';
import WebSocketClient from 'js/src/libs/websocket-client';

/**
 * The resolutionValue used when constructing a Luminary instance to back a
 * video draft preview in Sketch. Tuned per screen size so weaker devices
 * don't spend GPU/memory on resolution they can't display.
 */
export function getVideoDraftLuminaryResolution(): number {
    return window.screen?.availWidth <= SCREEN_WIDTH_THRESHOLD ? 300 : 500;
}

export const generateVoiceoverAsset = async ({
    text,
    voice,
    languageCode,
}: {
    text: string;
    voice: string;
    languageCode: string;
}) => {
    const response = await ApiService.textToVoiceoverAssetCreate({
        body: {
            text,
            voice_preset: voice,
            language_code: languageCode,
        },
    });
    const { async_operation_uuid } = response.data;
    const ws = WebSocketClient.getInstance();
    const result = await ws.waitForMessage<VoiceoverAssetResponse>(
        async_operation_uuid,
        TaskResultMessageTypeEnum.TASK_RESULT,
        AsyncOperationNameEnum.CONVERT_TEXT_TO_VOICEOVER_ASSET,
    );
    return {
        voiceoverAsset: ImmutableFromJS(result.audio_asset),
        transcript: {
            transcriptWords: result.transcript,
            language: result.language,
        } as AssetTranscript,
    };
};

export const createRecommendationAIVoiceover = async ({
    script,
    language,
    voiceId,
}: {
    script: string;
    language: LanguageEnum;
    voiceId: string;
}) => {
    if (!voiceId) {
        throw new Error('No AI voice selected for video plan');
    }
    const startTime = Date.now();
    const { voiceoverAsset, transcript } = await generateVoiceoverAsset({
        text: script,
        voice: voiceId,
        languageCode: language,
    });
    const endTime = Date.now();
    analytics.track('Voiceover Created for Video Plan', {
        duration: endTime - startTime,
        voiceoverDurationSeconds: voiceoverAsset.get('duration'),
        voiceId,
    });
    return { voiceoverAsset, transcript };
};
